
import React, { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { PeakDetail } from '../types';
import { GLOBAL_SEVERITY_MATRIX } from '../services/severityEngine';

interface ArticulationVisualizerProps {
  stuEnvelope: number[];
  stuPeaks: PeakDetail[];
  refPeaks?: PeakDetail[];
  duration: number;
  score: number | null;
  levelId?: number; 
}

/**
 * ArticulationVisualizer v2.5
 * integrity_enforcement_level: monitored
 * Provenance: DSP Transient Analysis (Calcul de la pente d'attaque sur fenêtre de 128ms).
 */
export const ArticulationVisualizer: React.FC<ArticulationVisualizerProps> = ({ 
    stuPeaks = [], 
    refPeaks = [],
    duration = 30,
    levelId = 4
}) => {
  const profile = (GLOBAL_SEVERITY_MATRIX[levelId] || GLOBAL_SEVERITY_MATRIX[4]).articulation;

  const chartData = useMemo(() => {
      // Simplification : On réduit le nombre de points pour la clarté (1 point par seconde max)
      const points = Math.max(10, Math.floor(duration)); 
      const data = [];
      const safeDuration = duration || 1;
      const bucketSize = safeDuration / points;

      for (let i = 0; i < points; i++) {
          const timeStart = i * bucketSize;
          const timeEnd = (i + 1) * bucketSize;
          
          // On prend le pic le plus significatif dans cette seconde
          const sPeak = stuPeaks.filter(p => p.time >= timeStart && p.time < timeEnd).sort((a,b) => b.intensity - a.intensity)[0];
          const rPeak = refPeaks.filter(p => p.time >= timeStart && p.time < timeEnd).sort((a,b) => b.intensity - a.intensity)[0];

          const stuVal = sPeak ? Math.min(100, Math.max(10, sPeak.intensity * 1200)) : 0;
          const refVal = rPeak ? Math.min(100, Math.max(10, rPeak.intensity * 1200)) : 0;

          let status = 'Correct';
          let color = '#34d399';
          if (stuVal > refVal * 1.4) { status = 'Lourde'; color = '#FF6261'; }
          if (stuVal < refVal * 0.6 && stuVal > 0) { status = 'Molle'; color = '#fbbf24'; }

          data.push({
              time: (timeStart ?? 0).toFixed(1),
              you: stuVal,
              master: refVal,
              status,
              color
          });
      }
      return data;
  }, [stuPeaks, refPeaks, duration]);

  return (
    <div className="bg-[#01080a] p-8 md:p-12 rounded-[64px] border border-white/5 shadow-2xl flex flex-col h-fit min-h-[600px] relative overflow-hidden animate-fade-in">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 shrink-0 z-20 gap-6">
            <div className="space-y-1">
                <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter flex items-center gap-4">
                    <span className="text-smart-accent">⚡</span> Attaque <span className="text-emerald-400">& Relâchement</span>
                </h3>
                <p className="text-[10px] text-slate-500 uppercase font-black tracking-[0.4em] ml-1">Comparaison des ondes de choc Master vs Vous</p>
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Visualiseur Simplifié */}
            <div className="lg:col-span-8 h-[350px] w-full bg-[#050b0d] rounded-[48px] border border-white/5 overflow-hidden relative shadow-inner p-6">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 20, bottom: 20, left: 10, right: 10 }}>
                        <XAxis dataKey="time" hide />
                        <YAxis hide domain={[0, 110]} />
                        <Bar dataKey="master" fill="#22d3ee" radius={[6,6,0,0]} opacity={0.15} barSize={25} isAnimationActive={false} />
                        <Bar dataKey="you" radius={[6,6,0,0]} barSize={15} style={{ transform: 'translateX(-5px)' }} isAnimationActive={true}>
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={entry.you > 0 ? 0.8 : 0.1} />
                            ))}
                        </Bar>
                       <Tooltip
  cursor={{ fill: "transparent" }}
  content={({ active, payload }) => {
    if (!active) return null;

    const p = payload ?? [];
    const d = (p[0]?.payload as any) ?? null;

    if (!d) return null;

    return (
      <div className="bg-black/90 border border-white/20 p-4 rounded-2xl backdrop-blur-md shadow-2xl">
        <p className="text-[10px] font-black text-slate-500 uppercase mb-2">
          Impact @ {d.time}s
        </p>
        <p className="text-xs text-white">
          Votre Pression:{" "}
          <span className="font-bold" style={{ color: d.color }}>
            {d.status}
          </span>
        </p>
      </div>
    );
  }}
/>

                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Documentation de la métrique (B2B Requirement) */}
            <div className="lg:col-span-4 space-y-6">
                <div className="bg-white/5 border border-white/10 p-6 rounded-[32px] space-y-4">
                    <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest italic">Provenance de la donnée</h4>
                    <p className="text-xs text-slate-400 leading-relaxed italic">
                        "L'IA n'invente pas votre note. Elle analyse la **vélocité du transient** (le temps que met le son pour passer du silence à son pic d'énergie). Un front montant rapide indique une attaque franche au médiator."
                    </p>
                    <div className="pt-4 border-t border-white/5 flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                            <span className="text-[9px] font-black text-slate-300 uppercase">Cible : Pente Master</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-smart-accent"></div>
                            <span className="text-[9px] font-black text-slate-300 uppercase">Mesure : Votre Touche</span>
                        </div>
                    </div>
                </div>

                <div className="bg-smart-gold/5 border border-smart-gold/20 p-6 rounded-[32px]">
                    <span className="text-[9px] font-black text-smart-gold uppercase tracking-[0.3em] block mb-2">Seuil L{levelId}</span>
                    <p className="text-[11px] text-slate-300 font-medium">
                        Minimum requis : **{(profile.minTransientRatio * 100).toFixed(0)}%** de relief par rapport au bruit de fond.
                    </p>
                </div>
            </div>
        </div>
    </div>
  );
};
